import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Move, ZoomIn, ZoomOut, Home, Save, Trash2, RotateCcw,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  Crosshair, Settings, X, Bookmark, Gauge
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PtzControls — Remote Pan/Tilt/Zoom control panel for IP cameras.
 * 
 * Commands are proxied through the backend to bypass CORS.
 * Supports Canon CGI, ONVIF HTTP, PTZ Optics, and generic CGI protocols.
 */
export default function PtzControls({ source, token, onClose }) {
  const [protocol, setProtocol] = useState(source?.config?.ptz_protocol || "generic_cgi");
  const [ptzUrl, setPtzUrl] = useState(source?.config?.ptz_url || "");
  const [authUser, setAuthUser] = useState(source?.config?.ptz_auth_user || "");
  const [authPass, setAuthPass] = useState(source?.config?.ptz_auth_pass || "");
  const [panSpeed, setPanSpeed] = useState(5);
  const [tiltSpeed, setTiltSpeed] = useState(5);
  const [zoomSpeed, setZoomSpeed] = useState(3);
  const [presets, setPresets] = useState([]);
  const [newPresetName, setNewPresetName] = useState("");
  const [showConfig, setShowConfig] = useState(!ptzUrl);
  const [protocols, setProtocols] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [moving, setMoving] = useState(false);
  const moveIntervalRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  // Load protocols + presets
  useEffect(() => {
    axios.get(`${API}/api/broadcast/ptz/protocols`, { headers }).then(r => setProtocols(r.data)).catch(() => {});
    if (source?.id) {
      axios.get(`${API}/api/broadcast/ptz/presets/${source.id}`, { headers }).then(r => setPresets(r.data)).catch(() => {});
    }
  }, [source?.id]);

  const sendCommand = useCallback(async (command, extra = {}) => {
    if (!ptzUrl) { toast.error("URL de controle PTZ non configuree"); return; }
    try {
      const res = await axios.post(`${API}/api/broadcast/ptz/command`, {
        camera_url: ptzUrl,
        protocol,
        command,
        auth_user: authUser || undefined,
        auth_pass: authPass || undefined,
        pan_speed: panSpeed,
        tilt_speed: tiltSpeed,
        zoom_speed: zoomSpeed,
        ...extra,
      }, { headers });
      setLastResult(res.data);
      if (!res.data.ok && command !== "stop") {
        toast.error(res.data.error || "Erreur PTZ");
      }
    } catch {
      toast.error("Erreur de communication PTZ");
    }
  }, [ptzUrl, protocol, authUser, authPass, panSpeed, tiltSpeed, zoomSpeed]);

  // Continuous move on press & hold
  const startMove = (direction) => {
    setMoving(true);
    sendCommand("move", { direction });
    moveIntervalRef.current = setInterval(() => {
      sendCommand("move", { direction });
    }, 400);
  };

  const stopMove = () => {
    setMoving(false);
    if (moveIntervalRef.current) {
      clearInterval(moveIntervalRef.current);
      moveIntervalRef.current = null;
    }
    sendCommand("stop");
  };

  const savePreset = async () => {
    if (!newPresetName.trim()) return;
    const nextId = presets.length + 1;
    // First save position on camera
    await sendCommand("preset_set", { preset_id: nextId });
    // Then save in DB
    try {
      const res = await axios.post(`${API}/api/broadcast/ptz/presets`, {
        source_id: source.id,
        name: newPresetName.trim(),
        preset_id: nextId,
      }, { headers });
      setPresets(prev => [...prev, res.data]);
      setNewPresetName("");
      toast.success(`Preset "${newPresetName}" sauvegarde`);
    } catch { toast.error("Erreur sauvegarde preset"); }
  };

  const recallPreset = async (preset) => {
    await sendCommand("preset_recall", { preset_id: preset.preset_id });
    toast.success(`Position "${preset.name}" chargee`);
  };

  const deletePreset = async (preset) => {
    try {
      await axios.delete(`${API}/api/broadcast/ptz/presets/${preset.id}`, { headers });
      setPresets(prev => prev.filter(p => p.id !== preset.id));
    } catch { toast.error("Erreur suppression"); }
  };

  // D-pad button helper
  const DPadBtn = ({ direction, icon: Icon, className = "", label }) => (
    <button
      onMouseDown={() => startMove(direction)}
      onMouseUp={stopMove}
      onMouseLeave={stopMove}
      onTouchStart={() => startMove(direction)}
      onTouchEnd={stopMove}
      className={`w-10 h-10 rounded-lg bg-[#0B1120] border border-[#1A2332] hover:border-[#0099CC] hover:bg-[#0099CC]/10 flex items-center justify-center text-[#94A3B8] hover:text-[#0099CC] transition-all active:scale-90 ${moving ? "ring-1 ring-[#0099CC]/30" : ""} ${className}`}
      title={label}
      data-testid={`ptz-${direction}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="bg-[#060B14] border border-[#1A2332] rounded-2xl overflow-hidden" data-testid="ptz-controls">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1A2332] bg-[#0B1120]">
        <div className="flex items-center gap-2">
          <Move className="w-4 h-4 text-[#F97316]" />
          <span className="text-white text-xs font-bold">PTZ — {source?.name || "Camera IP"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setShowConfig(!showConfig)} className="text-[#64748B] hover:text-white p-1 rounded" title="Configuration">
            <Settings className="w-3.5 h-3.5" />
          </button>
          {onClose && (
            <button onClick={onClose} className="text-[#64748B] hover:text-white p-1 rounded">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Config panel (collapsible) */}
      {showConfig && (
        <div className="p-3 border-b border-[#1A2332] space-y-2 bg-[#080E1A]" data-testid="ptz-config">
          <div>
            <label className="text-[9px] text-[#64748b] uppercase tracking-wider">URL de controle PTZ</label>
            <Input
              value={ptzUrl}
              onChange={e => setPtzUrl(e.target.value)}
              placeholder="http://192.168.1.100"
              className="bg-[#060B14] border-[#1A2332] text-white font-mono text-xs h-8"
              data-testid="ptz-url-input"
            />
          </div>
          <div>
            <label className="text-[9px] text-[#64748b] uppercase tracking-wider">Protocole</label>
            <select
              value={protocol}
              onChange={e => setProtocol(e.target.value)}
              className="w-full bg-[#060B14] border border-[#1A2332] text-white rounded-lg px-2 py-1.5 text-xs"
              data-testid="ptz-protocol-select"
            >
              {protocols.map(p => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] text-[#64748b] uppercase tracking-wider">Identifiant</label>
              <Input
                value={authUser}
                onChange={e => setAuthUser(e.target.value)}
                placeholder="admin"
                className="bg-[#060B14] border-[#1A2332] text-white text-xs h-8"
                data-testid="ptz-auth-user"
              />
            </div>
            <div>
              <label className="text-[9px] text-[#64748b] uppercase tracking-wider">Mot de passe</label>
              <Input
                type="password"
                value={authPass}
                onChange={e => setAuthPass(e.target.value)}
                placeholder="********"
                className="bg-[#060B14] border-[#1A2332] text-white text-xs h-8"
                data-testid="ptz-auth-pass"
              />
            </div>
          </div>
          {ptzUrl && (
            <Button onClick={() => { sendCommand("home"); setShowConfig(false); }} size="sm" className="w-full bg-[#0099CC] hover:bg-[#0088B5] text-white text-xs h-7" data-testid="ptz-test-connection">
              <Crosshair className="w-3 h-3 mr-1" /> Tester la connexion (Home)
            </Button>
          )}
          {lastResult && !lastResult.ok && (
            <p className="text-red-400 text-[10px]">{lastResult.error}</p>
          )}
          {lastResult?.ok && (
            <p className="text-[#10B981] text-[10px]">Connexion OK</p>
          )}
        </div>
      )}

      {/* D-Pad + Zoom */}
      <div className="p-3">
        <div className="flex gap-4 items-start justify-center">
          {/* D-Pad */}
          <div className="flex flex-col items-center gap-0.5" data-testid="ptz-dpad">
            <div className="flex gap-0.5">
              <div className="w-10 h-10" />
              <DPadBtn direction="up" icon={ChevronUp} label="Haut" />
              <div className="w-10 h-10" />
            </div>
            <div className="flex gap-0.5">
              <DPadBtn direction="left" icon={ChevronLeft} label="Gauche" />
              <button
                onClick={() => sendCommand("home")}
                className="w-10 h-10 rounded-lg bg-[#F97316]/10 border border-[#F97316]/30 flex items-center justify-center text-[#F97316] hover:bg-[#F97316]/20 transition-all active:scale-90"
                title="Position Home"
                data-testid="ptz-home"
              >
                <Home className="w-4 h-4" />
              </button>
              <DPadBtn direction="right" icon={ChevronRight} label="Droite" />
            </div>
            <div className="flex gap-0.5">
              <div className="w-10 h-10" />
              <DPadBtn direction="down" icon={ChevronDown} label="Bas" />
              <div className="w-10 h-10" />
            </div>
            {/* Diagonal controls */}
            <div className="flex gap-2 mt-1">
              <DPadBtn direction="upleft" icon={ChevronUp} className="rotate-[-45deg] w-7 h-7" label="Haut-Gauche" />
              <DPadBtn direction="upright" icon={ChevronUp} className="rotate-[45deg] w-7 h-7" label="Haut-Droite" />
              <DPadBtn direction="downleft" icon={ChevronDown} className="rotate-[45deg] w-7 h-7" label="Bas-Gauche" />
              <DPadBtn direction="downright" icon={ChevronDown} className="rotate-[-45deg] w-7 h-7" label="Bas-Droite" />
            </div>
          </div>

          {/* Zoom */}
          <div className="flex flex-col items-center gap-1" data-testid="ptz-zoom">
            <span className="text-[9px] text-[#64748b] uppercase tracking-wider mb-1">Zoom</span>
            <button
              onMouseDown={() => { sendCommand("zoom", { direction: "in" }); }}
              onMouseUp={() => sendCommand("stop")}
              onMouseLeave={() => sendCommand("stop")}
              className="w-10 h-10 rounded-lg bg-[#0B1120] border border-[#1A2332] hover:border-[#10B981] hover:bg-[#10B981]/10 flex items-center justify-center text-[#94A3B8] hover:text-[#10B981] transition-all active:scale-90"
              data-testid="ptz-zoom-in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onMouseDown={() => { sendCommand("zoom", { direction: "out" }); }}
              onMouseUp={() => sendCommand("stop")}
              onMouseLeave={() => sendCommand("stop")}
              className="w-10 h-10 rounded-lg bg-[#0B1120] border border-[#1A2332] hover:border-[#F59E0B] hover:bg-[#F59E0B]/10 flex items-center justify-center text-[#94A3B8] hover:text-[#F59E0B] transition-all active:scale-90"
              data-testid="ptz-zoom-out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Speed controls */}
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="ptz-speed">
          <div>
            <label className="text-[8px] text-[#64748b] uppercase flex items-center gap-1"><Gauge className="w-2.5 h-2.5" />Pan</label>
            <input type="range" min="1" max="10" value={panSpeed} onChange={e => setPanSpeed(+e.target.value)}
              className="w-full h-1 accent-[#0099CC]" data-testid="ptz-pan-speed" />
            <span className="text-[9px] text-[#94A3B8] text-center block">{panSpeed}</span>
          </div>
          <div>
            <label className="text-[8px] text-[#64748b] uppercase flex items-center gap-1"><Gauge className="w-2.5 h-2.5" />Tilt</label>
            <input type="range" min="1" max="10" value={tiltSpeed} onChange={e => setTiltSpeed(+e.target.value)}
              className="w-full h-1 accent-[#0099CC]" data-testid="ptz-tilt-speed" />
            <span className="text-[9px] text-[#94A3B8] text-center block">{tiltSpeed}</span>
          </div>
          <div>
            <label className="text-[8px] text-[#64748b] uppercase flex items-center gap-1"><Gauge className="w-2.5 h-2.5" />Zoom</label>
            <input type="range" min="1" max="10" value={zoomSpeed} onChange={e => setZoomSpeed(+e.target.value)}
              className="w-full h-1 accent-[#0099CC]" data-testid="ptz-zoom-speed" />
            <span className="text-[9px] text-[#94A3B8] text-center block">{zoomSpeed}</span>
          </div>
        </div>
      </div>

      {/* Presets */}
      <div className="border-t border-[#1A2332] p-3" data-testid="ptz-presets">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] text-[#64748b] uppercase tracking-wider font-bold flex items-center gap-1">
            <Bookmark className="w-3 h-3" /> Presets
          </span>
        </div>

        {presets.length > 0 && (
          <div className="space-y-1 mb-2">
            {presets.map(p => (
              <div key={p.id} className="flex items-center gap-2 group">
                <button
                  onClick={() => recallPreset(p)}
                  className="flex-1 text-left px-2.5 py-1.5 rounded-lg bg-[#0B1120] border border-[#1A2332] hover:border-[#0099CC] text-[#94A3B8] hover:text-white text-xs transition-all"
                  data-testid={`ptz-preset-${p.preset_id}`}
                >
                  <span className="text-[#F97316] font-mono mr-1.5">P{p.preset_id}</span>
                  {p.name}
                </button>
                <button
                  onClick={() => deletePreset(p)}
                  className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 p-1"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-1.5">
          <Input
            value={newPresetName}
            onChange={e => setNewPresetName(e.target.value)}
            placeholder="Nom du preset..."
            className="bg-[#0B1120] border-[#1A2332] text-white text-xs h-7 flex-1"
            data-testid="ptz-preset-name-input"
            onKeyDown={e => { if (e.key === "Enter") savePreset(); }}
          />
          <Button
            onClick={savePreset}
            disabled={!newPresetName.trim() || !ptzUrl}
            size="sm"
            className="bg-[#F97316] hover:bg-[#EA580C] text-white text-xs h-7 px-2"
            data-testid="ptz-save-preset"
          >
            <Save className="w-3 h-3 mr-1" /> Sauver
          </Button>
        </div>
      </div>
    </div>
  );
}
